// src/utils/openHouseRequests.js
// Pure rules for openHouseRequests rows (spec "Open House Scheduling, Rebuilt",
// 09/16/2026, section 3). No Firebase, no imports but the Minnesota clock, and no
// `new Date("YYYY-MM-DD")`: that parses as UTC midnight and shifts a day in Chicago.
//
// A request is one agent asking for one home on one date:
//   { id, dealId, propertyAddress, listingAgentId, listingAgentName,
//     agentName, agentBrokerage ("" on every request since 09/18/2026; never shown),
//     date "YYYY-MM-DD", startTime "HH:mm", endTime "HH:mm",
//     status: "requested" | "sent_to_seller" | "approved" | "declined", createdAt ms }
// "Expired" is never stored. A request whose date passed while it was still open reads
// as expired at read time (decision 12), the way Up For Grabs slots expire on Monday.

import { chicagoTodayYMD } from "./chicagoTime";

export const REQUEST_STATUS = {
  REQUESTED: "requested",
  SENT: "sent_to_seller",
  APPROVED: "approved",
  DECLINED: "declined",
};

const pad2 = (n) => String(n).padStart(2, "0");

// "YYYY-MM-DD" of today in Minnesota (America/Chicago), whatever zone the device is set to.
export function todayIso(now = Date.now()) {
  return chicagoTodayYMD(now);
}

function isOpenStatus(status) {
  return status === REQUEST_STATUS.REQUESTED || status === REQUEST_STATUS.SENT;
}

// Still waiting on somebody, and the proposed day is already behind us.
export function isRequestExpired(r, today) {
  const date = String(r?.date || "");
  const todayStr = String(today || "");
  return !!date && !!todayStr && date < todayStr && isOpenStatus(r?.status);
}

// "new" | "sent" | "approved" | "declined" | "expired"
export function requestState(r, today) {
  if (isRequestExpired(r, today)) return "expired";
  switch (r?.status) {
    case REQUEST_STATUS.SENT:
      return "sent";
    case REQUEST_STATUS.APPROVED:
      return "approved";
    case REQUEST_STATUS.DECLINED:
      return "declined";
    default:
      return "new";
  }
}

export function isOpenRequest(r, today) {
  const s = requestState(r, today);
  return s === "new" || s === "sent";
}

// The amber count: requests nobody has acted on yet.
export function countNewRequests(requests, today) {
  return (requests || []).filter((r) => requestState(r, today) === "new").length;
}

const STATE_ORDER = { new: 0, sent: 1, approved: 2, declined: 3, expired: 4 };

// New first (they need a click), then sent, approved, declined, expired. Inside a
// bucket by date, then start time, so the coming weekend reads top to bottom.
export function sortRequestsForListing(requests, today) {
  return [...(requests || [])].sort((a, b) => {
    const sa = STATE_ORDER[requestState(a, today)];
    const sb = STATE_ORDER[requestState(b, today)];
    if (sa !== sb) return sa - sb;
    const byDate = String(a?.date || "").localeCompare(String(b?.date || ""));
    if (byDate !== 0) return byDate;
    return String(a?.startTime || "").localeCompare(String(b?.startTime || ""));
  });
}

// One group per home, the home with the newest request first. Deal ids compare as
// strings: some old records hold the number.
export function groupRequestsByListing(requests, today) {
  const groups = new Map();
  (requests || []).forEach((r) => {
    const key = String(r?.dealId ?? "");
    if (!groups.has(key)) {
      groups.set(key, {
        dealId: key,
        propertyAddress: r?.propertyAddress || "",
        listingAgentId: r?.listingAgentId || "",
        listingAgentName: r?.listingAgentName || "",
        requests: [],
        newestAt: 0,
      });
    }
    const g = groups.get(key);
    g.requests.push(r);
    g.newestAt = Math.max(g.newestAt, Number(r?.createdAt) || 0);
  });
  return [...groups.values()]
    .sort((a, b) => b.newestAt - a.newestAt)
    .map((g) => ({
      dealId: g.dealId,
      propertyAddress: g.propertyAddress,
      listingAgentId: g.listingAgentId,
      listingAgentName: g.listingAgentName,
      requests: sortRequestsForListing(g.requests, today),
    }));
}

// ---- Overlap ---------------------------------------------------------------------
// Henry 09/25/2026: "I don't want things ever overlapping... An open house can overlap in the
// sense that if somebody is already doing it 1 to 3, somebody can do it 3 to 5." The server
// (functions-api/ohRequests.js) refuses a genuine clash now, so what this pair decides is what
// the team is warned about BEFORE they send a request to the seller. It has to answer exactly
// what the server answers, or the row promises a booking the server will not make.

// "13:45" -> 825 minutes; anything unreadable comes back null. Why the copy and not an import of
// src/utils/openHouseClash.js (09/25/2026): this file is deliberately import-free (it is read by
// the public hosting page), and openHouseClash pulls in openHouseHelpers. Same three lines, same
// rule, on purpose: functions-api/ohRequests.js minutesOf is the third. Change all three together.
function toMinutes(t) {
  const m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(String(t ?? ""));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// Two windows on the same day that share any minute. Back to back is fine (1 to 3 and 3 to 5 do
// not overlap), so the test is strictly `aStart < bEnd && aEnd > bStart`, never <= or >=.
// Read as minutes, not as text: "9:00" sorts after "13:00" as text, so a time typed without its
// leading zero used to hide a real clash. A window neither side can read counts as overlapping,
// the way the server counts it: warning once too often beats missing the twin.
export function windowsOverlap(a, b) {
  if (!a || !b) return false;
  const date = String(a.date || "");
  if (!date || date !== String(b.date || "")) return false;
  const aStart = toMinutes(a.startTime);
  const aEnd = toMinutes(a.endTime);
  const bStart = toMinutes(b.startTime);
  const bEnd = toMinutes(b.endTime);
  if (aStart === null || aEnd === null || bStart === null || bEnd === null) return true;
  return aStart < bEnd && aEnd > bStart;
}

// The open house this request runs into, so the Requests tab can warn before anyone sends it to
// the seller. A Cancelled one is not in the way; anything else is, INCLUDING one that already
// happened and has its feedback in. 09/25/2026: this used to step past a held open house, while
// the server's twin (functions-api/ohRequests.js overlappingOpenHouse) steps past Cancelled and
// nothing else, so the team saw no warning and then watched the approval be refused. The first
// match wins: one is enough to warn about.
export function overlappingOpenHouse(request, openHouses) {
  if (!request) return null;
  const dealId = String(request.dealId ?? "");
  return (
    (openHouses || []).find(
      (oh) => String(oh?.dealId ?? "") === dealId && oh?.status !== "Cancelled" && windowsOverlap(request, oh),
    ) || null
  );
}

// ---- The team fixing a time an agent asked for (Henry 09/25/2026) ------------------
// "If an agent requests a time and we need to edit it, we should be able to edit it from within the
// request pop-up window." The popup writes the corrected window straight onto the request, so the
// seller's email and the hosting agent's email both carry the time we actually mean. These three
// helpers are the rules that popup obeys; the popup itself does the asking.

// The one sentence that says what "no overlapping" leaves room for. The row's amber warning and the
// edit popup's refusal both end with it, so the team can never read two different rules for the same
// thing. Henry: "if somebody is already doing it 1 to 3, somebody can do it 3 to 5."
export const BACK_TO_BACK_NOTE = "One open house can start when another ends, but they cannot share a time.";

// Is this a day on the calendar? "YYYY-MM-DD" and nothing else (the house input format), and the day
// has to survive the round trip, so 02/29 in a year without one is refused rather than sliding to 03/01.
function isRealDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/**
 * The edited window, checked the way the rest of the feature checks one: a real day, a start and an
 * end we can read, and an end after the start. Back to back is fine BETWEEN open houses, but a window
 * that ends when it starts is not a window at all, so 12:00 to 12:00 is refused here.
 * Resolves to { ok: true } or { ok: false, error } with the sentence to show; the clash against what
 * is already on the calendar is a separate question (overlappingOpenHouse).
 */
export function validateRequestWindow(proposed) {
  const w = proposed && typeof proposed === "object" ? proposed : {};
  if (!isRealDate(w.date)) return { ok: false, error: "Pick a date for the open house." };
  const start = toMinutes(w.startTime);
  const end = toMinutes(w.endTime);
  if (start === null || end === null) return { ok: false, error: "Pick a start time and an end time." };
  if (end <= start) return { ok: false, error: "The end time has to be after the start time." };
  return { ok: true };
}

// Did the team move this request, rather than the agent asking for this slot? The stamp is
// request.timeChange { at, by, byName, fromDate, fromStartTime, fromEndTime }, written by the edit
// popup. Only a stamp that still holds the agent's whole window counts: half a trail is no trail.
export function teamChangedTime(r) {
  return !!agentAskedWindow(r);
}

// What the agent themselves asked for, or null when nobody moved it. A second edit never overwrites
// this (the writer keeps the first one), so it stays the window Dana actually typed.
export function agentAskedWindow(r) {
  const t = r && typeof r.timeChange === "object" && r.timeChange !== null ? r.timeChange : null;
  if (!t) return null;
  const date = String(t.fromDate || "");
  const startTime = String(t.fromStartTime || "");
  const endTime = String(t.fromEndTime || "");
  if (!date || !startTime || !endTime) return null;
  return { date, startTime, endTime };
}

// ---- Links -----------------------------------------------------------------------
// The app is a HashRouter: every public link is `${origin}/#/path?query`.
export function hostingLinkUrl(token, origin) {
  return `${origin}/#/host?t=${token}`;
}

// action: "approve" | "decline" (the button the seller clicked; the page confirms).
export function decisionUrl({ requestId, token, action, origin }) {
  return `${origin}/#/oh-decide?r=${requestId}&t=${token}&a=${action}`;
}

// Noon UTC the day after the proposed date: safely past midnight Central on both
// sides of daylight saving, so an emailed link dies the morning after the open house
// and never a day early. An unparseable date gives 0 (already expired: a dead link).
export function decisionTokenExpiresAt(dateIso) {
  const [y, m, d] = String(dateIso || "").split("-").map(Number);
  if (!y || !m || !d) return 0;
  return Date.UTC(y, m - 1, d + 1, 12, 0, 0);
}

// ---- Display formatting -----------------------------------------------------------
// House rules: dates MM/DD/YYYY (a weekday prefix is fine on client surfaces), times
// "1:00pm to 3:00pm". Never an em or en dash.
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dateParts(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return y && m && d ? { y, m, d } : null;
}

export function fmtMdy(iso) {
  const p = dateParts(iso);
  return p ? `${pad2(p.m)}/${pad2(p.d)}/${p.y}` : "";
}

// "Sun 09/27/2026". The weekday comes from the parts through Date.UTC, so the viewer's
// time zone can never move it a day.
export function fmtDayMdy(iso) {
  const p = dateParts(iso);
  if (!p) return "";
  const weekday = WEEKDAYS[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()];
  return `${weekday} ${fmtMdy(iso)}`;
}

// "13:00" -> "1:00pm", "00:30" -> "12:30am". Anything unparseable comes back as typed.
export function fmtTime12(hhmm) {
  const [hStr, mStr] = String(hhmm || "").split(":");
  const h = Number(hStr);
  if (!hStr || !Number.isFinite(h)) return String(hhmm || "");
  const period = h >= 12 ? "pm" : "am";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${(mStr || "00").padStart(2, "0")}${period}`;
}

export function fmtTimeRange(start, end) {
  if (!start || !end) return "";
  return `${fmtTime12(start)} to ${fmtTime12(end)}`;
}

// ---- Status pill for the Requests tab -----------------------------------------------
// Full class strings, so a row renders <span className={pill.className}>{pill.label}</span>.
// Same shape as StatusPill in OpenHousesTable.jsx; brand palette, never indigo.
const PILL_SHAPE = "text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap";
const PILLS = {
  new: { label: "New", className: `${PILL_SHAPE} bg-amber-50 text-amber-800` },
  sent: { label: "Sent to Seller", className: `${PILL_SHAPE} bg-blue-50 text-blue-700` },
  approved: { label: "Approved", className: `${PILL_SHAPE} bg-emerald-50 text-emerald-700` },
  declined: { label: "Declined", className: `${PILL_SHAPE} bg-slate-100 text-slate-500` },
  expired: { label: "Expired", className: `${PILL_SHAPE} bg-slate-100 text-slate-400` },
};

export function requestStatePill(state) {
  return PILLS[state] || PILLS.new;
}
